<script setup>
import { computed, onMounted, ref } from 'vue';
import { useAteliersStore } from '../../stores/ateliers';
import { Clock, MapPin, ArrowRight } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { optimizedUrl } from '../../utils/imageUrl';

const ateliersStore = useAteliersStore();
const { upcomingAteliers } = storeToRefs(ateliersStore);

// Atelier passé récent (≤ 2 jours), affiché en fallback quand aucun upcoming.
const recentPast = ref(null);

onMounted(async () => {
  if (upcomingAteliers.value.length === 0) await ateliersStore.fetchUpcoming(1);
  if (upcomingAteliers.value.length === 0) {
    try {
      recentPast.value = await ateliersStore.fetchLatestRecentPast(2);
    } catch (e) {
      console.warn('[AtelierHomeSection] fetchLatestRecentPast failed', e);
    }
  }
});

const next = computed(() => upcomingAteliers.value[0] || recentPast.value || null);
const isPast = computed(() => !upcomingAteliers.value[0] && !!recentPast.value);

const dayNum = computed(() => {
  if (!next.value?.date) return '';
  const d = next.value.date.toDate ? next.value.date.toDate() : new Date(next.value.date);
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric' }).format(d);
});
const monthShort = computed(() => {
  if (!next.value?.date) return '';
  const d = next.value.date.toDate ? next.value.date.toDate() : new Date(next.value.date);
  return new Intl.DateTimeFormat('fr-FR', { month: 'short' }).format(d).replace('.', '');
});
const weekday = computed(() => {
  if (!next.value?.date) return '';
  const d = next.value.date.toDate ? next.value.date.toDate() : new Date(next.value.date);
  return new Intl.DateTimeFormat('fr-FR', { weekday: 'long' }).format(d);
});

const seatsRemaining = computed(() =>
  next.value ? Math.max(0, (next.value.maxParticipants || 0) - (next.value.seatsTaken || 0)) : 0
);
const isFull = computed(() => next.value && seatsRemaining.value === 0);
const isAnnounced = computed(() => next.value?.status === 'announced');
// Urgency : 1 ou 2 places restantes → animation pulse + couleur rouge
const isLowStock = computed(() => !isFull.value && seatsRemaining.value > 0 && seatsRemaining.value <= 2);

const ctaLabel = computed(() => {
  if (isPast.value) return "Voir l'atelier";
  if (isAnnounced.value) return 'Découvrir';
  if (isFull.value) return 'Voir';
  return "M'inscrire";
});

const headerBadgeLabel = computed(() => (isPast.value ? 'Dernier atelier' : 'Prochain atelier'));

const coverUrl = computed(() => {
  if (!next.value?.coverImageUrl) return null;
  return optimizedUrl(next.value.coverImageUrl, { width: 600, quality: 80 }) || next.value.coverImageUrl;
});
</script>

<template>
  <section v-if="next" class="px-4 sm:px-6 py-4 sm:py-6">
    <div class="max-w-5xl mx-auto">
      <span
        class="inline-block text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm uppercase tracking-wide mb-2"
        :class="isPast ? 'bg-gray-500' : 'bg-[#e94591]'"
      >
        {{ headerBadgeLabel }}
      </span>
      <RouterLink
        :to="`/ateliers/${next.slug}`"
        class="block bg-white rounded-2xl border border-gray-200 hover:border-[#0077B6] overflow-hidden transition-colors"
      >
        <!-- ═══════ MOBILE LAYOUT (stacked) ═══════ -->
        <div class="sm:hidden">
          <!-- Image full-width with étiquette + date overlay -->
          <div class="relative aspect-[16/9] bg-[#0077B6]/10 overflow-hidden">
            <img
              v-if="coverUrl"
              :src="coverUrl"
              :alt="next.title"
              :class="['absolute inset-0 w-full h-full object-cover transition-all', isFull ? 'grayscale opacity-70' : '']"
              loading="lazy"
            />
            <img
              src="/img/ateliers/atelier-by.svg"
              alt="Atelier by Clo'Cook"
              class="absolute top-0 left-3 w-28 h-auto drop-shadow-md select-none pointer-events-none"
            />
            <!-- Prominent "Complet" overlay if full -->
            <span v-if="isFull" class="absolute top-3 right-3 bg-[#e94591] text-white text-xs font-extrabold uppercase tracking-wide px-3 py-1.5 rounded-full shadow-md">
              Complet
            </span>
            <div class="absolute bottom-3 right-3 bg-white/95 rounded-2xl px-4 py-2.5 backdrop-blur-sm shadow-md text-center">
              <p class="text-xs font-bold uppercase tracking-wide text-[#e94591] leading-none">{{ monthShort }}</p>
              <p class="text-3xl font-extrabold text-gray-900 leading-none mt-1">{{ dayNum }}</p>
              <p class="text-xs text-gray-600 capitalize leading-none mt-1">{{ weekday }}</p>
            </div>
          </div>
          <!-- Content + CTA -->
          <div class="p-4">
            <h3 class="text-lg font-bold text-gray-900 leading-tight">{{ next.title }}</h3>
            <p v-if="next.subtitle" class="text-base text-[#e94591] italic font-semibold mt-1">{{ next.subtitle }}</p>
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-sm text-gray-700">
              <span class="inline-flex items-center gap-1">
                <Clock class="w-4 h-4 text-[#0077B6]" />
                {{ next.startTime }} – {{ next.endTime }}
              </span>
              <span class="inline-flex items-center gap-1 min-w-0">
                <MapPin class="w-4 h-4 text-[#0077B6] shrink-0" />
                <span class="truncate">
                  <span class="font-semibold">{{ next.locationName }}</span><span v-if="next.locationAddress" class="text-gray-500"> · {{ next.locationAddress }}</span>
                </span>
              </span>
            </div>
            <div class="mt-4 flex items-center justify-between gap-2 flex-wrap">
              <span v-if="isPast" class="inline-flex items-center gap-1.5 text-[11px] bg-gray-100 text-gray-600 font-bold px-2 py-1 rounded-full border border-gray-200">
                <span class="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                Atelier terminé
              </span>
              <span v-else-if="isAnnounced" class="text-xs bg-[#e94591] text-white font-semibold px-2.5 py-1 rounded-full">Bientôt</span>
              <span v-else-if="isLowStock" class="inline-flex items-center gap-1.5 text-[11px] bg-pink-50 text-[#e94591] font-bold px-2 py-1 rounded-full border border-[#e94591]/30 animate-pulse">
                <span class="w-1.5 h-1.5 rounded-full bg-[#e94591]"></span>
                Reste {{ seatsRemaining }} place{{ seatsRemaining > 1 ? 's' : '' }}
              </span>
              <span v-else-if="isFull" class="text-xs bg-[#e94591] text-white font-semibold px-2 py-0.5 rounded-full">Complet</span>
              <span v-else class="inline-flex items-center gap-1.5 text-[11px] bg-[#ecfdf5] text-[#047857] font-bold px-2 py-1 rounded-full border border-[#047857]/30">
                <span class="w-1.5 h-1.5 rounded-full bg-[#047857]"></span>
                Inscriptions ouvertes
              </span>
              <span class="inline-flex items-center justify-center gap-1.5 bg-[#0077B6] text-white text-sm font-bold px-4 py-2.5 rounded-full whitespace-nowrap shadow-md">
                {{ ctaLabel }}
                <ArrowRight class="w-4 h-4" />
              </span>
            </div>
          </div>
        </div>

        <!-- ═══════ DESKTOP LAYOUT (horizontal) ═══════ -->
        <div class="hidden sm:flex items-stretch min-h-[180px]">
          <!-- Visual (left) -->
          <div class="relative w-64 shrink-0 bg-[#0077B6]/10 overflow-hidden">
            <img
              v-if="coverUrl"
              :src="coverUrl"
              :alt="next.title"
              :class="['absolute inset-0 w-full h-full object-cover transition-all', isFull ? 'grayscale opacity-70' : '']"
              loading="lazy"
            />
            <img
              src="/img/ateliers/atelier-by.svg"
              alt="Atelier by Clo'Cook"
              class="absolute top-0 left-3 w-32 h-auto drop-shadow-md select-none pointer-events-none"
            />
            <span v-if="isFull" class="absolute top-3 right-3 bg-[#e94591] text-white text-xs font-extrabold uppercase tracking-wide px-3 py-1.5 rounded-full shadow-md">
              Complet
            </span>
            <div class="absolute bottom-3 right-3 bg-white/95 rounded-2xl px-4 py-2.5 backdrop-blur-sm shadow-md text-center">
              <p class="text-xs font-bold uppercase tracking-wide text-[#e94591] leading-none">{{ monthShort }}</p>
              <p class="text-3xl font-extrabold text-gray-900 leading-none mt-1">{{ dayNum }}</p>
              <p class="text-xs text-gray-600 capitalize leading-none mt-1">{{ weekday }}</p>
            </div>
          </div>

          <!-- Content (middle) -->
          <div class="flex-1 min-w-0 p-4 flex flex-col justify-center">
            <h3 class="text-xl font-bold text-gray-900 leading-tight">{{ next.title }}</h3>
            <p v-if="next.subtitle" class="text-base sm:text-lg text-[#e94591] italic font-semibold mt-1">{{ next.subtitle }}</p>
            <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-2 text-sm text-gray-700">
              <span class="inline-flex items-center gap-1">
                <Clock class="w-4 h-4 text-[#0077B6]" />
                {{ next.startTime }} – {{ next.endTime }}
              </span>
              <span class="inline-flex items-center gap-1 min-w-0">
                <MapPin class="w-4 h-4 text-[#0077B6] shrink-0" />
                <span class="truncate">
                  <span class="font-semibold">{{ next.locationName }}</span><span v-if="next.locationAddress" class="text-gray-500"> · {{ next.locationAddress }}</span>
                </span>
              </span>
            </div>
          </div>

          <!-- CTA (right) -->
          <div class="flex flex-col items-center justify-center gap-1.5 px-5 shrink-0">
            <span class="inline-flex items-center justify-center gap-2 bg-[#0077B6] text-white text-base font-bold px-6 py-3 rounded-full whitespace-nowrap shadow-md">
              {{ ctaLabel }}
              <ArrowRight class="w-5 h-5" />
            </span>
            <span v-if="isPast" class="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 font-bold px-2 py-0.5 rounded-full border border-gray-200 whitespace-nowrap">
              <span class="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
              Atelier terminé
            </span>
            <span v-else-if="isAnnounced" class="text-xs bg-[#e94591] text-white font-semibold px-2.5 py-1 rounded-full">
              Bientôt
            </span>
            <span v-else-if="isLowStock" class="inline-flex items-center gap-1 text-xs bg-pink-50 text-[#e94591] font-bold px-2 py-0.5 rounded-full border border-[#e94591]/30 animate-pulse whitespace-nowrap">
              <span class="w-1.5 h-1.5 rounded-full bg-[#e94591]"></span>
              Reste {{ seatsRemaining }} place{{ seatsRemaining > 1 ? 's' : '' }}
            </span>
            <span v-else-if="isFull" class="text-xs bg-[#e94591] text-white font-semibold px-2 py-0.5 rounded-full">
              Complet
            </span>
            <span v-else class="inline-flex items-center gap-1 text-xs bg-[#ecfdf5] text-[#047857] font-bold px-2 py-0.5 rounded-full border border-[#047857]/30 whitespace-nowrap">
              <span class="w-1.5 h-1.5 rounded-full bg-[#047857]"></span>
              Inscriptions ouvertes
            </span>
          </div>
        </div>
      </RouterLink>
      <div class="mt-2 text-right px-1">
        <RouterLink to="/ateliers" class="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-[#e94591] font-semibold">
          Voir tous les ateliers
          <ArrowRight class="w-3 h-3" />
        </RouterLink>
      </div>
    </div>
  </section>
</template>
